import { useEffect, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/DateField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Panel } from "@/components/KpiCard";
import { api, errMsg } from "@/lib/api";

const PLATEFORMES = ["Airbnb", "Booking", "Lodgify"];
const TYPES = { pct: "% de (loyer + nettoyage)", fixe: "Montant fixe / réservation", annuel: "Frais annuel (abonnement)", menage: "Ménage inclus dans le montant / réservation" };
const UNITE = { pct: "%", fixe: "$", annuel: "$ / an", menage: "$ ménage inclus" };
const today = () => new Date().toISOString().slice(0, 10);
const libelle = (g) => (g.type === "pct" ? `${g.valeur} %` : `${Number(g.valeur).toFixed(2)} ${UNITE[g.type]}`).replace(".", ",");
const Th = ({ children }) => <th className="py-2 px-2 text-left text-[11px] font-mono uppercase tracking-wider text-slate-500">{children}</th>;

const Ligne = ({ g, i, set, del }) => (
  <tr className="border-t border-slate-100" data-testid={`comm-regle-row-${i}`}>
    <td className="p-2"><DateField value={g.debut} onChange={(v) => set(i, "debut", v)} className="w-40" data-testid={`comm-regle-debut-${i}`} /></td>
    <td className="p-2">
      <Select value={g.type} onValueChange={(v) => set(i, "type", v)}>
        <SelectTrigger className="h-9 w-56" data-testid={`comm-regle-type-${i}`}><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </td>
    <td className="p-2"><div className="flex items-center gap-1.5">
      <Input type="number" min="0" step="0.01" value={g.valeur} onChange={(e) => set(i, "valeur", e.target.value)} className="h-9 w-28" data-testid={`comm-regle-valeur-${i}`} />
      <span className="text-xs text-slate-500 whitespace-nowrap">{UNITE[g.type]}</span></div></td>
    <td className="p-2 text-right"><Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => del(i)} data-testid={`comm-regle-delete-${i}`}><Trash2 className="h-4 w-4" /></Button></td>
  </tr>
);

const EnVigueur = ({ rows, p }) => {
  const actives = rows.filter((g) => g.plateforme === p && g.debut && g.debut <= today()).sort((a, b) => a.debut.localeCompare(b.debut));
  const parType = Object.keys(TYPES).map((t) => actives.filter((g) => g.type === t).pop()).filter(Boolean);
  return (
    <p className="text-sm text-slate-700 mb-3" data-testid={`en-vigueur-${p.toLowerCase()}`}>
      En vigueur aujourd'hui : <span className="font-semibold">{parType.length ? parType.map(libelle).join(" · ") : "aucun paramètre"}</span>
    </p>
  );
};

const Onglet = ({ p, rows, set, del, add }) => (
  <TabsContent value={p} data-testid={`plateforme-tab-content-${p.toLowerCase()}`}>
    <EnVigueur rows={rows} p={p} />
    <div className="overflow-x-auto -mx-5">
      <SmartTable id={`commission-regles-${p.toLowerCase()}`} className="w-full text-sm min-w-[600px]" data-testid={`commission-regles-table-${p.toLowerCase()}`}>
        <thead><tr><Th>À partir du</Th><Th>Type</Th><Th>Valeur</Th><Th /></tr></thead>
        <tbody>{rows.map((g, i) => (g.plateforme === p ? <Ligne key={i} g={g} i={i} set={set} del={del} /> : null))}</tbody>
      </SmartTable>
    </div>
    <Button size="sm" variant="outline" className="mt-3" onClick={() => add(p)} data-testid={`comm-regle-add-${p.toLowerCase()}`}><Plus className="h-3.5 w-3.5 mr-1" />Ajouter un changement</Button>
  </TabsContent>
);

export const CommissionPanel = () => {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setRows(r.data.commission_regles)).catch((e) => toast.error(errMsg(e))); }, []);
  const set = (i, k, v) => setRows(rows.map((g, j) => (j === i ? { ...g, [k]: v } : g)));
  const del = (i) => setRows(rows.filter((_, j) => j !== i));
  const add = (p) => setRows([...rows, { plateforme: p, debut: today(), type: p === "Lodgify" ? "annuel" : "pct", valeur: "" }]);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/commission-regles", { regles: rows.map((g) => ({ ...g, valeur: Number(g.valeur) })) });
      setRows(data); toast.success("Paramètres des plateformes enregistrés");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  if (!rows) return <Loader2 className="h-6 w-6 animate-spin text-emerald-700" />;
  const valide = rows.every((g) => g.debut && g.valeur !== "");
  return (
    <Panel title="Commissions et frais par plateforme" testId="commission-panel"
      action={<Button size="sm" onClick={save} disabled={saving || !valide} className="bg-emerald-700 hover:bg-emerald-800" data-testid="commission-save-button">
        {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}Enregistrer</Button>}>
      <p className="text-xs text-slate-500 mb-4">Ajoutez une ligne datée à chaque changement. Pour chaque réservation, l'app prend la dernière ligne dont la date « À partir du » précède la date de réservation. Les frais annuels ne sont pas comparés aux réservations. « Ménage inclus » : quand le relevé de la plateforme n'a pas de colonne ménage (Booking), ce montant est séparé du loyer à l'import.</p>
      <Tabs defaultValue="Airbnb">
        <TabsList className="mb-4">{PLATEFORMES.map((p) => <TabsTrigger key={p} value={p} data-testid={`plateforme-tab-${p.toLowerCase()}`}>{p}</TabsTrigger>)}</TabsList>
        {PLATEFORMES.map((p) => <Onglet key={p} p={p} rows={rows} set={set} del={del} add={add} />)}
      </Tabs>
    </Panel>
  );
};
